// A session, entered (Brief 4 / P8) — the bottom of the left→right descent. You
// arrive on the OPERATORS: the operator-level chat (the orchestrator, the agent,
// and Supervisor / the arbiters / Signal), where you read and steer. A view-mode toggle flips
// to the LIVE TAIL: the raw execution stream, as if you were on that machine —
// the same session, watched closer. The confidence path is built in: watch the
// live tail closely → trust it → fall back to the calm Operators view.
//
// Assignment lives in the header: place this session in a project (or leave it
// Unassigned) so it groups under the right immovable goals. Reuses the session
// channel hook + presence on live transport; no mock data.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePresenceContext } from "@waytide/ui";
import {
  UNASSIGNED_PROJECT_ID,
  sessionProjectId,
  statusLabel,
} from "@waytide/ui";
import { useChannel } from "@waytide/ui";
import { StreamHealthBadge } from "@waytide/ui";
import { streamHealthCue } from "@waytide/ui";
import { ReadOnlyIndicator } from "@waytide/ui";
import { useReadWritePosture } from "@waytide/ui";
import { ProjectSchematic } from "@waytide/ui";
import { useSchematic } from "@waytide/ui";
import { SessionStatusPanel } from "@waytide/ui";
import { IncidentNotices, useIncidents } from "@waytide/ui";
import { useProjectsContext } from "../projects/ProjectsContext";
import { UNASSIGNED_LABEL } from "../projects/projectModel";
import { Conversation } from "../Conversation";
import { parseDecision, resolvedDecisionIds } from "../steering";
import { sessionName, sessionLabel, deriveSessionStatus } from "@waytide/core";
import type { StreamHealth, SteeringDecision } from "@waytide/core";

type ViewMode = "operators" | "live";

export function SessionView() {
  const { threadId: param } = useParams();
  const threadId = Number(param) || null;
  const { sessions, refresh: refreshPresence } = usePresenceContext();
  const session = sessions.find((s) => s.thread_id === threadId) ?? null;
  const { projects, assign } = useProjectsContext();
  const {
    messages,
    activity,
    health,
    lastEventAt,
    send,
    pending: outbox,
    retry,
    stop,
    resume,
  } = useChannel(threadId);
  const [mode, setMode] = useState<ViewMode>("operators");

  // The bound repo's schematic (Brief 5 / pt7) — the project's shape, fetched
  // once (server-cached). The read/write indicator points a write's location at it.
  const schematic = useSchematic(threadId);
  const repoFullName =
    schematic.kind === "ready" ? schematic.schematic.full_name : "";

  // Live status from presence; the optimistic steer echo clears when the
  // session's TRUE state lands over presence (never asserts stopped early).
  const status = session?.status ?? "";
  const stopped = status === "paused" || status === "halted";
  const [pending, setPending] = useState<"stop" | "resume" | null>(null);
  useEffect(() => setPending(null), [status]);

  // The read/write posture (Brief 5 / pt7 + pt9): READ-ONLY at rest; a named
  // momentary WRITE on a confirmed fact (the channel attaching = the hook
  // injection; a steer that landed over presence). `attached` = stream live —
  // the honest "we have a real channel" signal (never a claimed read-only when
  // the stream isn't actually flowing).
  const posture = useReadWritePosture({
    threadId,
    attached: health === "live",
    status,
    repoFullName,
  });

  // The technical status digest (Brief 5 / pt11) — the "catch me up technically"
  // view, derived (pure, in @waytide/core) from the SAME live data feeding the
  // schematic/stream above. Facts only; no predictions. Open steering decisions
  // are parsed from the message stream (the asides), un-resolved ones only.
  const openDecisions: SteeringDecision[] = (() => {
    const resolved = resolvedDecisionIds(messages);
    const out: SteeringDecision[] = [];
    for (const m of messages) {
      const d = parseDecision(m);
      if (d && !resolved.has(d.decision_id)) out.push(d);
    }
    return out;
  })();
  const statusDigest = deriveSessionStatus({
    health,
    lastEventAt,
    session,
    activity,
    openDecisions,
  });

  // The PUSH recovery notices (Brief 5 / pt10 + pt20): a caught-and-handled
  // incident (the live feed dropped + reconnected; a queued send that failed,
  // retried, or got parked) surfaced PROACTIVELY at the top of the operator
  // thread — the push complement to the pull-view SessionStatusPanel above. Fed
  // by the SAME live health + outbox the rest of this view renders; `failed` →
  // `parked` (the auto-retry gave up). FACTS only, classified in @waytide/core.
  const { notices: incidents, dismiss: dismissIncident } = useIncidents({
    health,
    outbox: outbox.map((e) => ({
      key: e.key,
      attempts: e.attempts,
      parked: e.failed ?? false,
    })),
  });

  // Assignment: the session's project from presence, editable here. The local
  // echo shows the chosen project instantly; presence catches up on re-read.
  const assignedId = session ? sessionProjectId(session) : UNASSIGNED_PROJECT_ID;
  const [assignedLocal, setAssignedLocal] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);
  const shownProject = assignedLocal ?? assignedId;

  // Drop the optimistic assign echo once presence reflects it — server truth
  // wins thereafter, the same reconcile the steer `pending` does on status.
  useEffect(() => {
    if (assignedLocal !== null && assignedId === assignedLocal) {
      setAssignedLocal(null);
    }
  }, [assignedId, assignedLocal]);

  async function onAssign(projectId: number) {
    if (threadId == null) return;
    setAssigning(true);
    try {
      const got = await assign(threadId, projectId);
      setAssignedLocal(got);
      // Re-read presence so this surface's sidebar + the project's session list
      // regroup from server truth at once (other surfaces catch up on the
      // session's next presence frame).
      refreshPresence();
    } finally {
      setAssigning(false);
    }
  }

  if (threadId == null) {
    return (
      <div className="wt-dash-pane">
        <p className="wt-dash-empty">Unknown session.</p>
      </div>
    );
  }

  return (
    <div className="wt-dash-pane wt-session-view">
      <header className="wt-dash-pane__head wt-session-view__head">
        <div className="wt-session-view__title-row">
          {/* The morph target: the session cell carries the same
              `session-<id>` name and BECOMES this title on the descent in. */}
          <h1
            className="wt-dash-pane__title"
            style={{ viewTransitionName: `session-${threadId}` }}
            title={`Session #${threadId}`}
          >
            {session ? sessionLabel(session) : sessionName(threadId)}
          </h1>
          <span className="wt-session-view__state">
            {statusLabel(status) || "—"}
          </span>
          {/* Stream-honesty (Brief 5): a calm cue that the LIVE feed is — or
              isn't — flowing, so a dead stream never looks like a quiet one. */}
          <StreamHealthBadge health={health} lastEventAt={lastEventAt} />
          {/* Read-only transparency (Brief 5 / pt7 + pt9): the persistent proof
              that Waytide is READ-ONLY ~99% of the time, naming a genuine write
              (the hook injection / a steer that reached the agent) momentarily. */}
          <ReadOnlyIndicator resolved={posture} />
          {stopped ? (
            <button
              type="button"
              className="wt-session-view__steer wt-session-view__steer--resume"
              onClick={() => {
                setPending("resume");
                void resume();
              }}
            >
              {pending === "resume" ? "Resuming…" : "Resume"}
            </button>
          ) : (
            <button
              type="button"
              className="wt-session-view__steer wt-session-view__steer--stop"
              onClick={() => {
                setPending("stop");
                void stop();
              }}
            >
              {pending === "stop" ? "Stopping…" : "Stop"}
            </button>
          )}
        </div>

        <div className="wt-session-view__meta">
          {/* Assignment — which project's goals this session answers to. */}
          <label className="wt-session-view__assign">
            <span className="wt-session-view__assign-label">Project</span>
            <select
              className="wt-session-view__assign-select"
              value={shownProject}
              disabled={assigning}
              onChange={(e) => void onAssign(Number(e.target.value))}
            >
              <option value={UNASSIGNED_PROJECT_ID}>{UNASSIGNED_LABEL}</option>
              {projects.map((p) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>

          {/* The view-mode toggle: the operator chat, or the raw live tail. */}
          <div
            className="wt-session-view__modes"
            role="tablist"
            aria-label="Session view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "operators"}
              className={`wt-session-view__mode${
                mode === "operators" ? " wt-session-view__mode--on" : ""
              }`}
              onClick={() => setMode("operators")}
            >
              Operators
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "live"}
              className={`wt-session-view__mode${
                mode === "live" ? " wt-session-view__mode--on" : ""
              }`}
              onClick={() => setMode("live")}
            >
              Live tail
            </button>
          </div>

          <Link className="wt-session-view__evidence" to={`/trace/${threadId}`}>
            Evidence ›
          </Link>
        </div>
      </header>

      {/* The PUSH recovery notices (Brief 5 / pt10 + pt20): a thin transient stack
          at the TOP of the thread, ABOVE the live region so it stays legible even
          while that region is dimmed. Calm + self-dismissing for a handled
          recovery; the reserved amber only for a send the auto-retry gave up on.
          Renders nothing when there's nothing to say — never competes with the
          chat. The push complement to the pull-view status panel below. */}
      <IncidentNotices notices={incidents} onDismiss={dismissIncident} />

      {/* The live region dims (~60%) whenever the stream is not live, so a stale
          feed is visibly stale — never masquerading as calm-live. The composer
          stays interactive (sends are queued + survive a drop, point 15). */}
      <div
        className={`wt-session-view__live${
          streamHealthCue(health, lastEventAt).dim ? " wt-stream-dim" : ""
        }`}
      >
        {mode === "operators" ? (
          <div className="wt-session-view__operators">
            {/* The project schematic (Brief 5 / pt7): the bound repo's shape at a
                glance — the project context this session answers to, and where a
                read/write op points its location. */}
            <ProjectSchematic state={schematic} />
            {/* The technical status report (Brief 5 / pt11): a compact, factual
                "catch me up" digest beside the operator chat — collapsed by
                default so it stays secondary to the conversation. */}
            <SessionStatusPanel digest={statusDigest} />
            <Conversation
              messages={messages}
              onSend={(b) => void send(b)}
              placeholder="Message this session…"
              emptyHint="No messages yet. Steer the session from here."
              draftScope={threadId}
              pending={outbox}
              onRetry={retry}
            />
          </div>
        ) : (
          <LiveTail activity={activity} health={health} />
        )}
      </div>
    </div>
  );
}

/** The raw execution stream — what the agent is doing right now, watched as if
 * you were on that machine. Newest last; calm rows, the trace kind + its
 * one-line summary. The empty copy is gated on health: it may only PROMISE a
 * live stream when the feed is actually live — when it isn't, it says so rather
 * than claim liveness it can't back. */
function LiveTail({
  activity,
  health,
}: {
  activity: import("@waytide/core").TraceEvent[];
  health: StreamHealth;
}) {
  if (activity.length === 0) {
    return (
      <p className="wt-dash-empty">
        {health === "live"
          ? "Nothing yet. When the session acts, it streams here live."
          : "Nothing to show — the live stream isn't connected right now."}
      </p>
    );
  }
  return (
    <ul className="wt-livetail">
      {activity.map((a, i) => (
        <li key={`${a.id}-${a.created_at}-${i}`} className="wt-livetail__row">
          <span className="wt-livetail__kind">{a.kind}</span>
          <span className="wt-livetail__summary">{a.summary}</span>
        </li>
      ))}
    </ul>
  );
}
